import React, { useMemo, useState } from 'react';

import { useTranslation } from 'next-i18next';

import type { Props as ButtonProps } from 'src/components/ui/Button';
import Button from 'src/components/ui/Button';
import stopPropagation from 'src/utils/stopPropagation';
import Icon from 'src/components/ui/Icon';
import useDynamicLogEvents from 'src/hooks/useDynamicLogEvents';
import { AnalyticsEvent } from 'src/utils/analytics';

interface Props extends ButtonProps {
    email: string;
    onEmailClicked?: () => void;
}

const HiddenEmailButton: React.FC<Props> = ({ email, onEmailClicked, ...buttonProps }) => {
    const { t } = useTranslation(['company']);
    const [showEmail, setShowEmail] = useState(false);
    const { logEventDynamic } = useDynamicLogEvents();

    const emailButton = useMemo(() => {
        const handleShowEmailClick = (e: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
            e.stopPropagation();
            e.preventDefault();
            logEventDynamic(AnalyticsEvent.SeeEmail);
            setShowEmail(true);
        };

        return (
            <Button
                startIcon={<Icon name="Mail" />}
                onMouseDown={stopPropagation}
                onClick={!showEmail ? handleShowEmailClick : undefined}
                color="primary"
                {...buttonProps}
            >
                {showEmail ? email : t('common:contactByEmail')}
            </Button>
        );
    }, [buttonProps, email, showEmail, t, logEventDynamic]);

    return showEmail ? (
        <a
            href={`mailto:${email}`}
            onMouseDown={stopPropagation}
            onClick={stopPropagation}
            data-target-id="email-button-anchor"
        >
            {emailButton}
        </a>
    ) : (
        emailButton
    );
};

export default HiddenEmailButton;
